/**
 * Canned content + UI enums for the Creative OS Home cockpit.
 *
 * The data views are LIVE now (see ./useCreativeOsHome). What remains here is the
 * scripted agent chat and presentation enums (nav keys, filter chips, density).
 * (nav keys, observation filter chips, starfield density). The chat is canned
 * pending its own live wiring (useBrandUniverseChat etc.); everything else is
 * pure presentation config with no backend.
 */

/** Nav is trimmed to surfaces backed by real data. */
export type NavKey = "home" | "universe" | "signals" | "strategy" | "calendar" | "settings";

export type Density = "calm" | "standard" | "busy";

// ---------------------------------------------------------------------------
// Agent chat — discriminated union on `kind` (user, say, think, act, ask, make, note).
// ---------------------------------------------------------------------------

/** A sub-agent voice; sets the speaker tag in the gutter. */
export type ChatAgent = "Strategy" | "Production" | "Culture" | "Search" | "Curator";

/** A `say` segment: plain prose, or a fact-name link into the universe graph. */
export type SaySegment = { t: string } | { factRef: string; label: string };

/** A clickable, keyboard-keyed decision option. Exactly one may be recommended. */
export type AskOption = {
  id: string;
  key: "A" | "B" | "C";
  label: string;
  hint?: string;
  recommended?: boolean;
};

/** The operator's (Mara's) question/command — the entry header the response hangs under. */
export type ChatUser = { id: string; kind: "user"; at: string; text: string };

/** The agent's spoken read — a curated digest in Manrope prose. */
export type ChatSay = {
  id: string;
  kind: "say";
  at: string;
  agent?: ChatAgent;
  turnId?: string;
  segments: SaySegment[];
  cite?: { source: string; extra?: number; conf?: number; srcCount?: number };
  signedBy?: string;
  streaming?: boolean;
};

/** Visible reasoning — collapsed to one mono line by default. */
export type ChatThink = {
  id: string;
  kind: "think";
  at: string;
  turnId?: string;
  steps: string[];
  ms?: number;
  open?: boolean;
  streaming?: boolean;
};

/** One instrument line = one thing the agent did. */
export type ChatAct = {
  id: string;
  kind: "act";
  at: string;
  turnId?: string;
  groupId?: string;
  tool: "SCAN" | "QUERY" | "SCORE" | "PROMOTE" | "DRAFT" | "CALENDAR" | (string & {});
  label: string;
  status: "run" | "ok" | "fail";
  meta?: string;
  progress?: number;
};

/** A decision prompt with clickable, keyboard-keyed choices; locks on choose. */
export type ChatAsk = {
  id: string;
  kind: "ask";
  at: string;
  turnId?: string;
  prompt: string;
  options: AskOption[];
  allowFreeText?: boolean;
  chosenId?: string;
  locked?: boolean;
  decidedBy?: string;
  decidedAt?: string;
};

/** A first-class result card for a produced deliverable. */
export type ChatMake = {
  id: string;
  kind: "make";
  at: string;
  turnId?: string;
  artifactKind: "brief" | "fact" | "move" | "calendar";
  title: string;
  lines: { label: string; text: string }[];
  primaryAction?: { label: string };
  signedBy?: string;
};

/** A faint full-width log divider recording a state change the chat caused. */
export type ChatNote = {
  id: string;
  kind: "note";
  at: string;
  text: string;
  tone?: "neutral" | "promoted";
};

export type ChatMessage =
  | ChatUser
  | ChatSay
  | ChatThink
  | ChatAct
  | ChatAsk
  | ChatMake
  | ChatNote;

// --- id + clock helpers for live (composer-driven) turns ---------------------
let _cid = 0;
/** Monotonic id for appended chat turns (preview-only, single instance). */
export const chatId = (): string => `cm-${_cid++}`;

// A mock wall clock that advances a minute per use, starting just after the
// scripted log (which ends at 09:04). Keeps appended turns deterministic and
// avoids a real Date.now() in render.
let _clock = 9 * 60 + 5; // 09:05
export function nextClock(): string {
  _clock += 1;
  const h = Math.floor(_clock / 60) % 24;
  const m = _clock % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** The scripted opening log — a complete, signed exchange exercising every
 *  turn type (think + act + say + ask + make + note). The decision is already
 *  locked, so the log shows the settled, signed ballot. */
export const INITIAL_MESSAGES: ChatMessage[] = [
  { id: "m-1", kind: "user", at: "09:01", text: "We keep seeing refill demand surface. Is it real enough to act on this week?" },
  {
    id: "m-2",
    kind: "think",
    at: "09:01",
    turnId: "t-1",
    ms: 1600,
    steps: [
      "Pulling every refill-related observation from the last 7 days.",
      "Two distinct signals: search up 38% across 6 markets, and creator content on refillable luxury.",
      "Cross-checking the competitive shelf before I commit to timing.",
    ],
  },
  { id: "m-3", kind: "act", at: "09:01", turnId: "t-1", groupId: "g-scan", tool: "SCAN", label: "search trends, 6 markets", status: "ok", meta: "9 reads · 0.6s" },
  { id: "m-4", kind: "act", at: "09:01", turnId: "t-1", groupId: "g-scan", tool: "SCAN", label: "press monitor, 14 outlets", status: "ok", meta: "14 src · 0.5s" },
  { id: "m-5", kind: "act", at: "09:01", turnId: "t-1", groupId: "g-scan", tool: "SCORE", label: "refill demand candidate", status: "ok", meta: "88 / 100 · 6 src" },
  {
    id: "m-6",
    kind: "say",
    at: "09:02",
    turnId: "t-1",
    agent: "Strategy",
    segments: [
      { factRef: "f8", label: "Refill demand" },
      { t: " is real and fresh. Queries are up 38% across your six core markets and you have nothing refillable live, so it is the strongest candidate sitting in your signals right now." },
    ],
    cite: { source: "trends.example", extra: 5, conf: 88, srcCount: 6 },
  },
  { id: "m-7", kind: "user", at: "09:02", text: "Why this week specifically, and not next month?" },
  {
    id: "m-8",
    kind: "say",
    at: "09:02",
    turnId: "t-2",
    agent: "Strategy",
    segments: [
      { t: "Because the shelf just opened. A heritage rival paused its citrus line this week and three tier-one outlets are running the " },
      { factRef: "f9", label: "Grasse harvest" },
      { t: ", so you have a 30 to 45 day window before it narrows." },
    ],
    cite: { source: "press monitor · 3 outlets · window 30 to 45d" },
  },
  {
    id: "m-9",
    kind: "ask",
    at: "09:03",
    turnId: "t-3",
    prompt: "Promote refill demand to a constant the agents reason from?",
    options: [
      { id: "opt-a", key: "A", label: "Promote now. Agents brief and plan around it.", hint: "6 src · safe", recommended: true },
      { id: "opt-b", key: "B", label: "Hold for one more sweep.", hint: "+3 to 4 days" },
      { id: "opt-c", key: "C", label: "Decline. Not a fact yet." },
    ],
    allowFreeText: true,
    chosenId: "opt-a",
    locked: true,
    decidedBy: "Mara",
    decidedAt: "09:03",
  },
  { id: "m-10", kind: "note", at: "09:03", tone: "promoted", text: "PROMOTED TO UNIVERSE · refill demand → Channels" },
  { id: "m-11", kind: "act", at: "09:03", turnId: "t-3", groupId: "g-promote", tool: "PROMOTE", label: "refill demand → Channels", status: "ok", meta: "constant · 0.4s" },
  {
    id: "m-12",
    kind: "make",
    at: "09:03",
    turnId: "t-3",
    artifactKind: "brief",
    title: "Refill waitlist · teaser + capture",
    lines: [
      { label: "Hook", text: "the refill your neroli has been waiting for" },
      { label: "Channels", text: "IG + TikTok, 2 slots" },
      { label: "Pulls from", text: "refill demand (new fact), DTC first, neroli signature" },
      { label: "Slot", text: "Thu +4d, status OPEN" },
    ],
    primaryAction: { label: "Open the brief →" },
    signedBy: "Drafted by Atlas · for Sandy to review",
  },
  {
    id: "m-13",
    kind: "say",
    at: "09:04",
    turnId: "t-3",
    agent: "Curator",
    segments: [
      { t: "Done. Refill demand is now a constant and the waitlist brief is queued for Thursday. I have flagged the layering question as your next candidate. Want me to draft that one too?" },
    ],
    cite: { source: "next: DRAFT layering explainer · 1 candidate pending" },
  },
];

/** Build the agent's response sequence to a free-typed message. A user turn
 *  always gets a folded reasoning line then a spoken read, and (per the input)
 *  an instrument action, an open decision, or a produced artifact. Canned, no
 *  model. Returns the agent turns only; the shell appends the user turn first. */
export function cannedTurns(text: string): ChatMessage[] {
  const q = (text || "").toLowerCase();
  const at = nextClock();
  const turnId = chatId();
  const think = (steps: string[], ms: number): ChatThink => ({ id: chatId(), kind: "think", at, turnId, steps, ms });
  const say = (segments: SaySegment[], cite?: ChatSay["cite"], agent: ChatAgent = "Strategy"): ChatSay => ({ id: chatId(), kind: "say", at, turnId, agent, segments, cite });

  if (q.includes("promote") || q.includes("candidate") || q.includes("should i") || q.includes("decide")) {
    return [
      think([
        "Re-reading the candidate's evidence and how many distinct sources back it.",
        "Confirming it is fresh this week and not already a constant.",
        "Framing the promote, hold or decline tradeoff for you.",
      ], 1500),
      { id: chatId(), kind: "act", at, turnId, tool: "SCORE", label: "candidate strength", status: "ok", meta: "88 / 100 · 6 src" },
      say([{ t: "It clears the bar. Six distinct sources, fresh this week, and nothing live against it. Promoting makes it a constant the agents brief and plan around." }], { source: "fact-candidates · 1 pending" }),
      {
        id: chatId(),
        kind: "ask",
        at,
        turnId,
        prompt: "Promote it to a constant now, or hold for one more sweep?",
        options: [
          { id: chatId(), key: "A", label: "Promote now. Make it a constant.", hint: "6 src · safe", recommended: true },
          { id: chatId(), key: "B", label: "Hold for one more sweep.", hint: "+3 to 4 days" },
          { id: chatId(), key: "C", label: "Decline. Not a fact yet." },
        ],
        allowFreeText: true,
      },
    ];
  }

  if (q.includes("chang") || q.includes("new") || q.includes("week")) {
    return [
      think([
        "Diffing this week's universe against last Thursday's.",
        "Three facts entered: refill demand, the Grasse harvest, the layering question.",
      ], 1300),
      { id: chatId(), kind: "act", at, turnId, tool: "SCAN", label: "universe diff, 7 days", status: "ok", meta: "3 new · 0.4s" },
      say([{ t: "Since Thursday: refill demand up 38% across six markets, the Grasse harvest trending across three outlets, and layering surfacing as the top buyer question on your product pages." }], { source: "observations · last 7 days", srcCount: 6 }, "Search"),
    ];
  }

  if (q.includes("brief") || q.includes("draft")) {
    return [
      think([
        "Pulling the constants this brief should hang on.",
        "Drafting a hook, channels and a slot from the open calendar window.",
      ], 1700),
      { id: chatId(), kind: "act", at, turnId, tool: "DRAFT", label: "waitlist brief", status: "ok", meta: "1 draft · 0.7s" },
      {
        id: chatId(),
        kind: "make",
        at,
        turnId,
        artifactKind: "brief",
        title: "Refill waitlist · teaser + capture",
        lines: [
          { label: "Hook", text: "the refill your neroli has been waiting for" },
          { label: "Channels", text: "IG + TikTok, 2 slots" },
          { label: "Slot", text: "Thu +4d, status OPEN" },
        ],
        primaryAction: { label: "Open the brief →" },
        signedBy: "Drafted by Atlas · for Sandy to review",
      },
    ];
  }

  if (q.includes("why")) {
    return [
      think([
        "Tracing the timing back to the competitive shelf.",
        "A rival paused its citrus line, so the window is open now.",
      ], 1200),
      say([{ t: "Because the signal is fresh and the shelf just opened. A heritage rival paused its citrus line this week, so acting now captures the search lift while it is still climbing." }], { source: "press monitor · window 30 to 45d" }),
    ];
  }

  if (q.includes("refill")) {
    return [
      think(["Pulling the refill candidate and its evidence."], 900),
      say([
        { factRef: "f8", label: "Refill demand" },
        { t: " rose 38% in your core markets and you have no refillable product live. It is the candidate at the top of your signals to review." },
      ], { source: "trends.example", srcCount: 6 }),
    ];
  }

  return [
    think(["Pulling the relevant observations.", "Composing a short read."], 900),
    say([{ t: "On it. I pulled the relevant observations: the strongest open signal is still refill demand, fresh this week with nothing live against it." }], { source: "observations · last 7 days" }),
  ];
}

/** Starfield particle counts behind the universe, by density. */
export const DENSITY_PARTS: Record<Density, { parts: number; gap: number }> = {
  calm: { parts: 26, gap: 2.4 },
  standard: { parts: 46, gap: 1.4 },
  busy: { parts: 72, gap: 0.85 },
};
